import { cn } from '@/lib/cn';
import { Badge, type BadgeColor } from '@heidi/design-system/badge';
import { Card } from '@heidi/design-system/card';
import {
  TypographyCaption,
  TypographyCaptionBold,
  typographyVariant,
} from '@heidi/design-system/typography';
import {
  Activity,
  Database,
  FileText,
  Link2,
  type LucideIcon,
  UserRound,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { defineMessages, type IntlShape, useIntl } from 'react-intl';

import type { EhrLinkDebugDetails } from './ehr-link-debug-details';

const messages = defineMessages({
  sessionContext: {
    defaultMessage: 'Session context',
    id: 'components.dev.integration.ehrLinks.result.sessionContext',
  },
  sessionLink: {
    defaultMessage: 'Session link',
    id: 'components.dev.integration.ehrLinks.result.sessionLink',
  },
  sessionLinkDescription: {
    defaultMessage: 'IntegrationSessionLink returned by /integrations/links/sessions.',
    id: 'components.dev.integration.ehrLinks.result.sessionLinkDescription',
  },
  patientLink: {
    defaultMessage: 'Patient profile link',
    id: 'components.dev.integration.ehrLinks.result.patientLink',
  },
  patientLinkDescription: {
    defaultMessage:
      'No patient-link read endpoint is exposed. These identifiers come from session.patient_profile.',
    id: 'components.dev.integration.ehrLinks.result.patientLinkDescription',
  },
  consultNoteLink: {
    defaultMessage: 'Consult-note link',
    id: 'components.dev.integration.ehrLinks.result.consultNoteLink',
  },
  consultNoteLinkDescription: {
    defaultMessage:
      'IntegrationConsultNoteLink returned for session.latest_consult_note_id.',
    id: 'components.dev.integration.ehrLinks.result.consultNoteLinkDescription',
  },
  legacyFields: {
    defaultMessage: 'Legacy session EHR fields',
    id: 'components.dev.integration.ehrLinks.result.legacyFields',
  },
  legacyFieldsDescription: {
    defaultMessage:
      'Deprecated ehr_* values stored directly on the session object. Compare these with the link records above.',
    id: 'components.dev.integration.ehrLinks.result.legacyFieldsDescription',
  },
  legacy: {
    defaultMessage: 'Legacy',
    id: 'components.dev.integration.ehrLinks.result.legacy',
  },
  found: {
    defaultMessage: 'Found',
    id: 'components.dev.integration.ehrLinks.result.found',
  },
  notFound: {
    defaultMessage: 'Not found',
    id: 'components.dev.integration.ehrLinks.result.notFound',
  },
  error: {
    defaultMessage: 'Error',
    id: 'components.dev.integration.ehrLinks.result.error',
  },
  notApplicable: {
    defaultMessage: 'Not applicable',
    id: 'components.dev.integration.ehrLinks.result.notApplicable',
  },
  linked: {
    defaultMessage: 'Linked',
    id: 'components.dev.integration.ehrLinks.result.linked',
  },
  notLinked: {
    defaultMessage: 'Not linked',
    id: 'components.dev.integration.ehrLinks.result.notLinked',
  },
  noPayload: {
    defaultMessage: 'No linked record payload.',
    id: 'components.dev.integration.ehrLinks.result.noPayload',
  },
});

type LinkResult =
  | EhrLinkDebugDetails['modern_links']['session_link']
  | EhrLinkDebugDetails['modern_links']['consult_note_link'];

interface DetailCardProps {
  id: string;
  title: string;
  description?: string;
  icon: LucideIcon;
  badge?: { color: BadgeColor; label: string };
  children: ReactNode;
}

function DetailCard({
  id,
  title,
  description,
  icon: Icon,
  badge,
  children,
}: Readonly<DetailCardProps>) {
  return (
    <Card role="region" aria-labelledby={id} className="p-3 hover:shadow-none">
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <Icon className="size-4 shrink-0 text-text-tertiary" aria-hidden="true" />
          <TypographyCaptionBold id={id} className="truncate text-text-primary">
            {title}
          </TypographyCaptionBold>
        </div>
        {badge ? (
          <Badge size="xs" color={badge.color}>
            {badge.label}
          </Badge>
        ) : null}
      </div>
      {description ? (
        <TypographyCaption className="mt-1.5 text-text-tertiary">
          {description}
        </TypographyCaption>
      ) : null}
      <div className="mt-3">{children}</div>
    </Card>
  );
}

function formatDebugValue(value: unknown): string {
  if (value == null) return '-';
  if (typeof value === 'string') return value;
  if (
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    typeof value === 'bigint'
  ) {
    return `${value}`;
  }
  return JSON.stringify(value) ?? '-';
}

function DebugFields({ fields }: { fields: Record<string, unknown> }) {
  return (
    <dl className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] gap-x-3 gap-y-1.5">
      {Object.entries(fields).map(([label, value]) => (
        <div key={label} className="contents">
          <dt
            className={cn(
              typographyVariant({ variant: 'caption' }),
              'break-all text-text-tertiary'
            )}
          >
            {label}
          </dt>
          <dd
            className={cn(
              typographyVariant({ variant: 'caption' }),
              'break-all text-end font-mono text-text-secondary'
            )}
          >
            {formatDebugValue(value)}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function getStatusBadge(result: LinkResult, intl: IntlShape) {
  switch (result.status) {
    case 'found':
      return { color: 'green', label: intl.formatMessage(messages.found) } as const;
    case 'not_found':
      return { color: 'neutral', label: intl.formatMessage(messages.notFound) } as const;
    case 'error':
      return { color: 'red', label: intl.formatMessage(messages.error) } as const;
    case 'not_applicable':
      return {
        color: 'neutral',
        label: intl.formatMessage(messages.notApplicable),
      } as const;
  }
}

function LinkResultContent({ result }: { result: LinkResult }) {
  const metadata = {
    ...('http_status' in result ? { http_status: result.http_status } : {}),
    ...('message' in result ? { message: result.message } : {}),
    ...('reason' in result ? { reason: result.reason } : {}),
  };
  const payload = 'data' in result ? result.data : undefined;
  const intl = useIntl();

  return (
    <div className="space-y-2">
      {Object.keys(metadata).length > 0 ? <DebugFields fields={metadata} /> : null}
      {payload != null ? (
        <pre
          className={cn(
            typographyVariant({ variant: 'caption' }),
            'scrollbar-subtle max-h-48 overflow-auto rounded-md bg-background-tertiary p-2 font-mono text-text-secondary'
          )}
        >
          {JSON.stringify(payload, null, 2)}
        </pre>
      ) : (
        <TypographyCaption className="text-text-tertiary">
          {intl.formatMessage(messages.noPayload)}
        </TypographyCaption>
      )}
    </div>
  );
}

export function EhrLinkDetailsResult({
  details,
}: Readonly<{ details: EhrLinkDebugDetails }>) {
  const intl = useIntl();
  const patientLink = details.modern_links.patient_link;

  return (
    <div className="space-y-2">
      <DetailCard
        id="ehr-debug-session-context"
        title={intl.formatMessage(messages.sessionContext)}
        icon={Activity}
      >
        <DebugFields fields={details.session_context} />
      </DetailCard>

      <DetailCard
        id="ehr-debug-session-link"
        title={intl.formatMessage(messages.sessionLink)}
        description={intl.formatMessage(messages.sessionLinkDescription)}
        icon={Link2}
        badge={getStatusBadge(details.modern_links.session_link, intl)}
      >
        <LinkResultContent result={details.modern_links.session_link} />
      </DetailCard>

      <DetailCard
        id="ehr-debug-patient-link"
        title={intl.formatMessage(messages.patientLink)}
        description={intl.formatMessage(messages.patientLinkDescription)}
        icon={UserRound}
        badge={{
          color: patientLink.data ? 'green' : 'neutral',
          label: intl.formatMessage(
            patientLink.data ? messages.linked : messages.notLinked
          ),
        }}
      >
        <DebugFields
          fields={{
            link_source: patientLink.source,
            ...(patientLink.data ?? {}),
          }}
        />
      </DetailCard>

      <DetailCard
        id="ehr-debug-consult-note-link"
        title={intl.formatMessage(messages.consultNoteLink)}
        description={intl.formatMessage(messages.consultNoteLinkDescription)}
        icon={FileText}
        badge={getStatusBadge(details.modern_links.consult_note_link, intl)}
      >
        <LinkResultContent result={details.modern_links.consult_note_link} />
      </DetailCard>

      <DetailCard
        id="ehr-debug-legacy-fields"
        title={intl.formatMessage(messages.legacyFields)}
        description={intl.formatMessage(messages.legacyFieldsDescription)}
        icon={Database}
        badge={{ color: 'yellow', label: intl.formatMessage(messages.legacy) }}
      >
        <DebugFields fields={details.legacy_session_fields} />
      </DetailCard>
    </div>
  );
}
